import React, { useEffect, useRef, useState } from 'react'
import { connect as connectRedux } from 'react-redux'
import { getBoundActions } from 'actions'
import { getPrimary } from 'services/languages'
import { ES } from 'services/languages/constants'
import { URL_HELP_CENTER_ES, URL_HOME } from 'services/url/constants'
import { get as getConfig } from 'config'
import { Map, List } from 'immutable'
import _get from 'lodash/get'
import ButtonSignUp, { BUTTON_SIGN_UP_TYPE_LINK } from 'components/ButtonSignUp'
import Link, { TARGET_BLANK } from 'components/Link'
import Loading, { LOADING_ICON_BLUE_DARK_BG_WHITE } from 'components/Loading'
import ErrorMessage from 'components/ErrorMessage'
import FormsyForm from 'formsy-react'
import { TYPE_LOGIN } from 'services/dialog'
import { FORM_INPUT_NAME_EMAIL } from 'services/form'
import { EmailInput } from 'components/FormInput.v2'
import { H2, HEADING_TYPES } from 'components/Heading'
import Button, { TYPES } from 'components/Button'
import {
  USER_LOGIN_NEW_TO_GAIA_CLICK_GA4_EVENT,
  LOG_IN_EMAIL_REQUESTED_GA4_EVENT,
  LOGIN_GA4_EVENT,
} from 'services/event-tracking'
import { historyRedirect } from 'services/navigation'

const { features } = getConfig()
const POLLING_INTERVAL = 5000
const CODE_LENGTH = 6

function LoginWithCode (props) {
  const {
    user,
    staticText,
    remoteLoginCode,
    requestMagicLink,
    setDefaultGa4Event,
    consumeRemoteLoginCode,
    setOverlayDialogVisible,
  } = props

  const isProcessing = remoteLoginCode.get('approveRemoteLoginCodeProcessing')

  let supportLink = 'https://gaiasupportcenter.zendesk.com/hc/en-us/requests/new'
  const userLanguage = getPrimary(user.getIn(['data', 'language'], List()))
  const langIsEs = userLanguage === ES

  const pollingTimeout = useRef(null)

  const [email, setEmail] = useState('')
  const [canSubmit, setCanSubmit] = useState(false)
  const [loginCode, setLoginCode] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)
  const [codeValue, setCodeValue] = useState('')
  const [expiresAt, setExpiresAt] = useState(null)

  if (langIsEs && _get(features, ['userLanguage', 'esZendeskHelpLink'])) {
    supportLink = URL_HELP_CENTER_ES
  }

  const onClickRegister = () => {
    // GA4 event
    setDefaultGa4Event(USER_LOGIN_NEW_TO_GAIA_CLICK_GA4_EVENT)

    setOverlayDialogVisible(null, false)
  }

  const onLoginWithPasswordClick = () => {
    setOverlayDialogVisible(TYPE_LOGIN)
  }

  const onEmailSubmit = async (formData) => {
    setDefaultGa4Event(LOG_IN_EMAIL_REQUESTED_GA4_EVENT)

    const res = await requestMagicLink({ email: formData.email, language: userLanguage })
    if (res.success) {
      setEmail(formData.email)
      setLoginCode(res.remoteLoginCode)
      setExpiresAt(res.expiresAt ? new Date(res.expiresAt).getTime() : null)
      setErrorMessage(null)
    } else {
      setLoginCode(null)
      setExpiresAt(null)
      setErrorMessage(res.message === 'emailNotExists' ? staticText.get('emailNotExists') : errorMessage)
    }
  }

  const handleCodeChange = async (event) => {
    const { history, auth, path } = props
    const value = _get(event, 'target.value', '')
    setErrorMessage(null)
    setCodeValue(value.replace(/\D/ig, ''))
    if (value && /[0-9]{6}/.test(value)) {
      event.target.blur()
      const { success } = await consumeRemoteLoginCode({ remoteLoginCode: value })
      if (success) {
        // GA4 event
        setDefaultGa4Event(LOGIN_GA4_EVENT.mergeDeep({
          ui_location: 'code entry',
        }))
        setOverlayDialogVisible(null, false)
        // redirect to the current path or member home
        historyRedirect({
          url: path,
          language: userLanguage,
          history,
          auth,
        })
      } else {
        setErrorMessage(staticText.get('wrongCode'))
      }
    }
  }

  const switchToCodeLogin = () => {
    setErrorMessage(null)
    setLoginCode(null)
    setCodeValue('')
    setExpiresAt(null)
  }

  const startConsumeCodePolling = (approvedLoginCode) => {
    if (pollingTimeout.current) {
      clearTimeout(pollingTimeout.current)
    }

    const isLinkExpired = expiresAt ? expiresAt - POLLING_INTERVAL < new Date().getTime() : true

    if (approvedLoginCode && !isLinkExpired) {
      pollingTimeout.current = setTimeout(async () => {
        const { success } = await consumeRemoteLoginCode({ remoteLoginCode: approvedLoginCode })
        if (success) {
          setOverlayDialogVisible(null, false)
          global.location.reload()
        } else {
          startConsumeCodePolling(approvedLoginCode)
        }
      }, POLLING_INTERVAL)
    }
  }

  useEffect(() => {
    startConsumeCodePolling(loginCode)
    return () => {
      if (pollingTimeout.current) {
        clearTimeout(pollingTimeout.current)
      }
    }
  }, [loginCode])

  if (loginCode) {
    return (
      <div className="login">
        <p className="login__text">{staticText.get('codeWasSent').replace(/\$\{ email \}/, '')}</p>
        <p className="login__text login__text--email">{email}</p>
        <div className="login__code-field-wrapper">
          <div className="login__code-field">
            <div className="login__code-field-mask">
              {new Array(CODE_LENGTH).fill(null).map((_v, index) => (
                <div key={index} className="login__code-field-mask-item"></div>
              ))}
            </div>
            <div className="login__code-input-wrapper">
              <input
                disabled={isProcessing}
                className="login__code-input"
                aria-labelledby="input-code-label"
                type="text"
                name="input-code"
                tabIndex="3"
                value={codeValue}
                onChange={handleCodeChange}
                autoComplete="one-time-code"
                inputMode="numeric"
                maxLength={CODE_LENGTH}
              />
            </div>
          </div>
          {errorMessage && <ErrorMessage errorMessages={[errorMessage]} />}
        </div>
        <p className="login__text">
          {staticText.get('noCode')}{' '}
          <Button
            type={TYPES.LINK}
            className="login__support-link"
            onClick={switchToCodeLogin}
          >
            {staticText.get('sendAgain')}
          </Button>
        </p>
        <p className="login__text">
          {staticText.get('or')}{' '}
          <Button
            type={TYPES.LINK}
            className="login__support-link"
            onClick={onLoginWithPasswordClick}
          >
            {staticText.get('singupWithPassword')}
          </Button>
        </p>
        {isProcessing ? (
          <div className="login__loading">
            <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
          </div>
        ) : (
          <div className="login__loading-placeholder" />
        )}
      </div>
    )
  }

  return (
    <div className="login">
      <H2 as={HEADING_TYPES.H4} className="login__title">
        {staticText.get('logIn')}
      </H2>
      <FormsyForm
        onValidSubmit={onEmailSubmit}
        onValid={() => setCanSubmit(true)}
        onInvalid={() => setCanSubmit(false)}
      >
        <EmailInput
          className="login-username__username"
          disabled={isProcessing}
          validations={{ isEmail: true }}
          validationErrors={{ isEmail: staticText.get('emailValidError') }}
          validationTimeout={750}
          label={staticText.get('email')}
          name={FORM_INPUT_NAME_EMAIL}
          tabIndex="3"
          value={email}
          required
          autofocus
          autocomplete="email"
        />
        {errorMessage && <ErrorMessage errorMessages={[errorMessage]} />}
        <div className="login__submit-wrapper">
          <Button
            type={TYPES.PRIMARY}
            submit
            className="login__login-code-button"
            disabled={!canSubmit}
            stacked
          >
            {staticText.get('sendCode')}
          </Button>
          <p className="login__text">{staticText.get('or')}</p>
          <Button
            type={TYPES.SECONDARY}
            className="login__login-code-button"
            stacked
            onClick={onLoginWithPasswordClick}
          >
            {staticText.get('logInWithPassword')}
          </Button>
        </div>
        <div className="login__register">
          <ButtonSignUp
            className="login__register-link"
            onClick={onClickRegister}
            type={BUTTON_SIGN_UP_TYPE_LINK}
            text={staticText.get('newToGaia')}
            scrollToTop
          />
        </div>
        <div className="login__support">
          <Link
            to={supportLink}
            target={TARGET_BLANK}
            className="login__support-link"
            onClick={() => setOverlayDialogVisible(null)}
          >
            {staticText.get('contactCustomerService')}
          </Link>
        </div>
        {isProcessing ? (
          <div className="login__loading">
            <Loading visible icon={LOADING_ICON_BLUE_DARK_BG_WHITE} />
          </div>
        ) : (
          <div className="login__loading-placeholder" />
        )}
      </FormsyForm>
    </div>
  )
}

export default connectRedux(({
  staticText,
  user,
  auth,
  remoteLoginCode,
  resolver,
}) => ({
  staticText: staticText.getIn(['data', 'login', 'data'], Map()),
  auth,
  user,
  remoteLoginCode,
  path: resolver.getIn(['data', 'path']) || URL_HOME,
}), (dispatch) => {
  const actions = getBoundActions(dispatch)
  return {
    requestMagicLink: actions.remoteLoginCode.doRequestMagicLink,
    consumeRemoteLoginCode: actions.remoteLoginCode.doConsumeRemoteLoginCode,
    setDefaultGa4Event: actions.eventTracking.setDefaultGa4Event,
    setOverlayDialogVisible: actions.dialog.setOverlayDialogVisible,
  }
})(LoginWithCode)
